import Constants from 'src/utilities/constants'
import axios from 'axios'
import create from './create'
import _ from 'lodash'
import {OK} from 'http-status-codes'
import {ptracGetEntities} from 'src/utilities/ptracApi'
import {make} from 'vuex-pathify'
import {sortByName, kvpsForSelectBasic, objectById} from 'src/utilities/utils'
import {get} from 'src/utilities/axios2'
import {addHeaders} from 'src/utilities/authHeader'

let state = {
  search: {
    query: {},
  },
  processDefinitions: [],
  processDefinitionInstanceCount: 0,
  processDefinitionXml: null,
  activityInstances: [],
  tasks: [],
  deployments: [],
  deleteWithCascade: false,
  ptracProcessDefinitions: [],
  deploymentsByDepartment: {},
  processDefinitionsByDepartment: {}
}

const getters = make.getters(state)

getters.ptracProcessDefinitionById = (state) => (id) => {
  return objectById(state.ptracProcessDefinitions, id)
}

getters.processDefinitionsByDeploymentId = (state) => (deploymentId) => {
  return _.filter(state.processDefinitionsWithInstanceCounts, (processDefinition) => processDefinition.deploymentId === deploymentId)
}

getters.ptracKvpsForSelect = (state) => {
  return kvpsForSelectBasic(state.ptracProcessDefinitions, 'name', 'name', 'id')
}

getters.processDefinitionsForSelect = (state) => {
  const uniqueNames = []
  const uniqueNamesForSelect = []
  if (state.processDefinitions) {
    state.processDefinitions.forEach((pd) => {
      if (!uniqueNames.includes(pd.name)) {
        uniqueNames.push(pd.name)
      }
    })

    uniqueNames.sort()

    uniqueNames.forEach((name) => {
      uniqueNamesForSelect.push({
        label: name,
        value: name
      })
    })
  }
  return uniqueNamesForSelect
}

getters.processDefinitionsForSelectByDepartmentId = (state) => (departmentIds) => {
  const uniqueNamesForSelect = []

  if (Array.isArray(departmentIds)) {
    const uniqueNames = []

    if (!state.processDefinitionsByDepartment) {
      state.processDefinitionsByDepartment = {}
    }

    departmentIds.forEach((id) => {
      if (state.processDefinitionsByDepartment[id]) {
        state.processDefinitionsByDepartment[id].forEach((processDefinitionName) => {
          if (!uniqueNames.includes(processDefinitionName)) {
            uniqueNames.push(processDefinitionName)
          }
        })
      }
    })
    uniqueNames.sort()

    uniqueNames.forEach((name) => {
      uniqueNamesForSelect.push({
        label: name,
        value: name
      })
    })
  }

  return uniqueNamesForSelect
}

getters.deploymentsByDepartmentById = (state) => {
  return (departmentId) => {
    if (!state.deploymentsByDepartment.hasOwnProperty(departmentId)) {
      return null
    }
    return state.deploymentsByDepartment[departmentId]
  }
}

const mutations = make.mutations(state)

mutations.processDefinitions = (state, payload) => {
  state.processDefinitions = payload
}

mutations.deployments = (state, payload) => {
  state.deployments = payload
}

mutations.deleteDeployment = (state, id) => {
  state.deployments = _.filter(state.deployments, (deployment) => deployment.id !== id)
}

mutations.resetDeploymentsForDepartment = (state, departmentId) => {
  state.deploymentsByDepartment[departmentId] = []
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions: {
    // details here: https://stackoverflow.com/questions/31166178/retrieving-a-process-instance-diagram-in-camunda
    // Camunda API Endpoint
    async fetchActivityInstances({commit}, processInstanceId) {
      const url = process.env.VUE_APP_PTRAC_API_URI + '/camunda/process-instance/' + processInstanceId + '/activity-instances'
      const response = await get(url, await addHeaders())

      commit('activityInstances', []) // if the code above returns 404, old cruft can be displayed

      if (OK === response.status) {
        commit('activityInstances', response.data)
      }

      return response
    },
    // Camunda API Endpoint
    async fetchTasks({commit}, processInstanceId) {
      const url = process.env.VUE_APP_PTRAC_API_URI + '/camunda/task?processInstanceId=' + processInstanceId
      const response = await get(url, await addHeaders())

      if (OK === response.status) {
        commit('tasks', response.data)
      }
      return response
    },
    // Camunda API Endpoint
    async fetchProcessDefinitionXml({commit}, processDefinitionId) {
      const url = process.env.VUE_APP_PTRAC_API_URI + '/camunda/process-definition/' + processDefinitionId + '/xml'
      const response = await get(url, await addHeaders())
      if (OK === response.status) {
        commit('processDefinitionXml', response.data.bpmn20Xml)
      }
      return response
    },
    // Camunda API Endpoint
    async fetchAndDownloadProcessDefinition({state, dispatch}, processDefinitionId) {
      const url = process.env.VUE_APP_PTRAC_API_URI + '/camunda/process-definition/' + processDefinitionId
      const response = await get(url, await addHeaders())
      if (OK === response.status) {
        dispatch('downloadProcessDefinition', response.data)
      }
      return response
    },
    // Camunda API Endpoint
    async fetchProcessDefinitionWithProcessInstanceCount({commit}, id) {
      const url = process.env.VUE_APP_PTRAC_API_URI + '/camunda/process-instance/count?processDefinitionId=' + id
      const response = await get(url, await addHeaders())
      if (OK === response.status) {
        commit('processDefinitionInstanceCount', response.data.count)
      }
    },
    // PTRAC API Endpoint
    async fetchAllPtracDeployments({state}) {
      const url = 'camunda-deployments/search/findByActive?active=true&sort=name&name.dir=asc&size=500'
      const response = await ptracGetEntities(url)

      if (OK === response.status) {
        const deployments = response.data

        // group deployments by department id - start
        const deploymentsByDepartment = {}
        deployments.forEach((deployment) => {
          if (!deploymentsByDepartment.hasOwnProperty(deployment.departmentId)) {
            deploymentsByDepartment[deployment.departmentId] = []
          }
          deploymentsByDepartment[deployment.departmentId].push(deployment)
        })
        state.deploymentsByDepartment = deploymentsByDepartment
        // group deployments by department id - end

        // group process definitions by department id - start
        const processDefinitionsByDepartment = {}
        deployments.forEach((deployment) => {
          if (!processDefinitionsByDepartment.hasOwnProperty(deployment.departmentId)) {
            processDefinitionsByDepartment[deployment.departmentId] = []
          }
          if (_.get(deployment, 'processDefinitions[0].name')) {
            const jobName = deployment.processDefinitions[0].name
            if (!processDefinitionsByDepartment[deployment.departmentId].includes(jobName)) {
              processDefinitionsByDepartment[deployment.departmentId].push(jobName)
            }
          }
        })
        state.processDefinitionsByDepartment = processDefinitionsByDepartment
        // group process definitions by department id - end
      }
      return response
    },
    // PTRAC API Endpoint
    async fetchPtracCamundaDeploymentsByDepartmentAdmin({state}, departmentId) {
      const url = 'camunda-deployments/search/findByDepartmentIdAndActiveOrderByNameAsc?departmentId=' + departmentId + '&active=true'
      const response = await ptracGetEntities(url)

      if (OK === response.status) {
        state.deploymentsByDepartment[departmentId] = response.data
      }

      return response
    },
    // PTRAC API Endpoint
    async fetchPtracProcessDefinitionsByDepartment({state}, departmentId) {
      const url = 'camunda-deployments/search/findByDepartmentId?departmentId=' + departmentId + '&size=1000'
      const response = await ptracGetEntities(url)

      if (OK === response.status) {
        const deployments = response.data

        let processDefinitions = {}

        deployments.forEach((deployment) => {
          deployment.processDefinitions.forEach((pd) => {
            // only keep the latest version (loaded last, overwrites preceding versions)
            if (deployment.active) {
              processDefinitions[pd.key] = pd
            }
          })
        })

        state.ptracProcessDefinitions = sortByName(Object.values(processDefinitions))
      }
      return response
    },
    downloadProcessDefinition({state}, processDefinition) {
      let blob = new Blob([state.processDefinitionXml], {type: 'application/xml'})
      let link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = processDefinition.name + '-v' + processDefinition.version + '.xml'
      link.click()
    },
    // Camunda API Endpoint
    async deleteDeployment({state, commit, dispatch}, id) {
      const url = process.env.VUE_APP_PTRAC_API_URI + '/camunda/deployment/' + id + '?cascade=true'
      try {
        await dispatch('deletePtracDeployment', id) // delete from PTRAC
        commit('deleteDeployment', id)

        await axios.delete(url, { headers: await addHeaders() }) // delete from Camunda
        this.$eventHub.$emit('refreshCamundaDeployments')
        return {
          result: Constants.RETURN_CODES.SUCCESS
        }
      } catch (err) {
        return {
          result: Constants.RETURN_CODES.ERROR,
          error: err
        }
      }
    },
    // PTRAC API Endpoint
    async deletePtracDeployment({state}, id) {
      const url = process.env.VUE_APP_PTRAC_API_URI + '/camunda-deployments/' + id
      try {
        await axios.delete(url, { headers: await addHeaders() })
        return {
          result: Constants.RETURN_CODES.SUCCESS
        }
      } catch (err) {
        return {
          result: Constants.RETURN_CODES.ERROR,
          error: err
        }
      }
    },
    // PTRAC API Endpoint
    async archiveDeployment({commit}, deployment) {
      const url = process.env.VUE_APP_PTRAC_API_URI + '/camunda-deployments/' + deployment.id
      try {
        deployment.active = false
        await axios.put(url, deployment, { headers: await addHeaders() })
        commit('deleteDeployment', deployment.id)
        this.$eventHub.$emit('refreshCamundaDeployments')

        return {
          result: Constants.RETURN_CODES.SUCCESS
        }
      } catch (err) {
        return {
          result: Constants.RETURN_CODES.ERROR,
          error: err
        }
      }
    },
    // PTRAC API Endpoint
    async deleteDeploymentsByDepartment({}, departmentId) {
      const url = process.env.VUE_APP_PTRAC_API_URI + '/camunda-deployments/deleteByDepartment?departmentId=' + departmentId
      return axios.delete(url, { headers: await addHeaders() })
    }
  },
  modules: {
    create
  }
}
